import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ApiError } from '../api/client';
import { isAdmin } from '../api/profile';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fetchHealthIssues, fetchHealthTrends, fetchReadiness } from '../features/ops';
import { DashboardPageHeader, DashboardDataTable } from '../shared/ui';
import type { CriticalIssue, HealthTrendDay } from '../features/ops';

const SOURCE_KEYS = ['http', 'scheduler', 'external_api'] as const;
const SOURCE_LABEL: Record<(typeof SOURCE_KEYS)[number], string> = {
  http: 'HTTP',
  scheduler: 'Scheduler',
  external_api: 'External API',
};

const SOURCE_COLORS: Record<(typeof SOURCE_KEYS)[number], string> = {
  http: '#0d6efd',
  scheduler: '#198754',
  external_api: '#fd7e14',
};

function mapApiError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Some details are invalid. Please check and try again.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Admin access is required.';
    return err.message || 'Something went wrong.';
  }
  return 'Something went wrong. Please try again.';
}

const SystemHealthPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [issues, setIssues] = useState<CriticalIssue[]>([]);
  const [trends, setTrends] = useState<HealthTrendDay[]>([]);
  const [readiness, setReadiness] = useState<{ ok: boolean; database: string } | null>(null);

  const chartData = useMemo(() => {
    return trends.map((d) => ({
      date: d.date,
      http: d.bySource.http ?? 0,
      scheduler: d.bySource.scheduler ?? 0,
      external_api: d.bySource.external_api ?? 0,
    }));
  }, [trends]);

  const loadDashboard = useCallback(async (opts?: { notifySuccess?: boolean }) => {
    setLoading(true);
    setBanner(null);
    try {
      const [issueList, trendDays, ready] = await Promise.all([
        fetchHealthIssues(),
        fetchHealthTrends(30),
        fetchReadiness(),
      ]);
      setIssues(issueList);
      setTrends(trendDays);
      setReadiness(ready);
      if (opts?.notifySuccess) {
        showToast('System health data refreshed.');
      }
    } catch (err) {
      setBanner(mapApiError(err));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!isAdmin(profile)) return;
    void loadDashboard();
  }, [loadDashboard, profile]);

  if (!isAdmin(profile)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-fluid px-4" style={{ maxWidth: 1100 }}>
      <DashboardPageHeader
        title="System health"
        description="Operational metrics and recent critical issues. Visible to administrators only."
        icon="bi bi-heart-pulse"
      >
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => void loadDashboard({ notifySuccess: true })}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </DashboardPageHeader>

      {banner && (
        <div className="alert alert-danger" role="alert">
          {banner}
        </div>
      )}

      {readiness && (
        <div className="card shadow-sm mb-4">
          <div className="card-body py-3">
            <span className="fw-semibold me-2">Database</span>
            <span className={`badge ${readiness.ok ? 'bg-success' : 'bg-danger'}`}>{readiness.database}</span>
          </div>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <span className="fw-semibold">Critical issues (last 30 days, UTC)</span>
        </div>
        <div className="card-body" style={{ height: 320 }}>
          {chartData.length === 0 ? (
            <p className="text-muted mb-0">No trend data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} width={36} />
                <Tooltip />
                <Legend formatter={(value) => SOURCE_LABEL[value as keyof typeof SOURCE_LABEL] ?? value} />
                {SOURCE_KEYS.map((key) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stackId="1"
                    stroke={SOURCE_COLORS[key]}
                    fill={SOURCE_COLORS[key]}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <span className="fw-semibold">Recent issues (up to 30)</span>
        </div>
        <div className="card-body p-0">
          <DashboardDataTable size="sm" aria-label="Recent critical issues">
            <thead>
              <tr>
                <th>Time (UTC)</th>
                <th>Source</th>
                <th>Message</th>
                <th>Status</th>
                <th>Path</th>
              </tr>
            </thead>
            <tbody>
              {issues.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-muted py-4 text-center">
                    No critical issues recorded.
                  </td>
                </tr>
              ) : (
                issues.map((row) => (
                  <tr key={row.id}>
                    <td className="text-nowrap small">{new Date(row.createdAt).toISOString().replace('T', ' ').slice(0, 19)}</td>
                    <td>
                      <span className="badge bg-secondary">{row.source}</span>
                    </td>
                    <td className="small">{row.message}</td>
                    <td className="small">{row.statusCode ?? '—'}</td>
                    <td className="small text-break">{row.path ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </DashboardDataTable>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthPage;
